<template>
  <OCard
    :attribute="attribute"
    :image="image"
  >
    <div class="text-left text-neutral-800 h-full flex flex-col justify-center">
      <div class="pt-6 px-7 mb-6 md:px-4">
        <template v-if="hasSlot">
          <slot />
        </template>
        <template v-else>
          <h4 class="o-heading-2 mb-1" v-html="$t('Loyalty.Card.Title', { price: localPriceMontserrat, maxPrice })" />
          <h4 class="o-body" v-html="$t('Loyalty.Card.SubTitle', { price: localPriceMontserrat, maxPrice })" />
        </template>
      </div>
      <div class="px-4">
        <div class="blue-shade rounded px-3 py-6">
          <LinkSharer
            :link="link"
            @click="$emit('click', $event)"
          >
            <p class="mb-6 o-heading-4" v-text="$t('Loyalty.Card.HowItWorks', { price: localPriceMontserrat, maxPrice })" />
            <div
              v-for="(usp, i) in usps"
              :key="`${usp.text}-${i}`"
              class="mb-9"
            >
              <div class="flex items-center">
                <div>
                  <OSvg :svg="usp.svg" class="w-6 fill-neutral-800" />
                </div>
                <p class="o-body ml-6" v-text="usp.text" />
              </div>
            </div>
          </LinkSharer>
          <span class="o-caption block mt-9" v-text="$t('Loyalty.Card.Note', { price: localPrice, maxPrice })" />
        </div>
      </div>
    </div>
  </OCard>
</template>

<script>
import { Currencies, WomenCountries } from '@/enums/general'
import { addLocalCurrency } from '@/utils/general'

const OCard = () => import('@/components/OCard')
const LinkSharer = () => import('@/components/LinkSharer')
const OSvg = () => import('@/components/OSvg')

export default {
  name: 'LoyaltyReferralLinkSharer',
  components: {
    OCard,
    LinkSharer,
    OSvg
  },
  props: {
    link: {
      type: String,
      default: ''
    },
    loginButtonText: {
      type: String,
      default: 'Loyalty.LinkSharing.SignUp'
    },
    description: {
      type: String,
      default: ''
    },
    country: {
      type: String,
      default: 'nl'
    },
    price: {
      type: Number,
      default: 10
    },
    image: {
      type: Object,
      default: undefined
    },
    attribute: {
      type: Object,
      default: null,
      validator: value => ['name', 'value'].every(prop => value.hasOwnProperty(prop))
    }
  },
  computed: {
    hasSlot () {
      return !!this.$slots.default && !!this.$slots.default[0]
    },
    localPrice () {
      return addLocalCurrency(this.price, this.country, 'EUR').replace(/\s/g, '').replace(',00', '').replace('.00', '')
    },
    localPriceMontserrat () {
      return `<span class="montserrat-currency">${addLocalCurrency(this.price, this.country, 'EUR').replace(/\s/g, '').replace(',00', '').replace('.00', '')}</span>`
    },
    loginLink () {
      return `<a class="underline" href="/login">${this.$t('Loyalty.LinkSharing.Here')}</a>`
    },
    bodyText () {
      return this.description ? this.description : 'Loyalty.Card.Description'
    },
    maxPrice () {
      return addLocalCurrency((this.price * 2), this.country, Currencies[this.country]).replace(/\s/g, '').replace(',00', '').replace('.00', '')
    },
    usps () {
      const path = this.isWomenCountry ? 'Loyalty.Card.WomenUSPS' : 'Loyalty.Card.USPS'
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      const variables = {
        localPrice: this.localPrice,
        localPriceMontserrat: this.localPriceMontserrat,
        price: this.localPriceMontserrat,
        maxPrice: this.maxPrice
      }

      return items.map((_, index) => ({
        svg: this.$t(`${path}[${index}].Svg`),
        text: this.$t(`${path}[${index}].Text`, variables)
      }))
    },
    isWomenCountry () {
      return WomenCountries.includes(this.country)
    }
  }
}
</script>

<style lang="postcss" scoped>
.ocard {
  .blue-shade {
    background-color: #E9EEFC;
  }

  >>> .input-wrapper {
    .input-icon {
      @apply bg-newBlue-400 text-white !important;

      .bg-black {
        @apply bg-newBlue-400 text-white border-newBlue-400 !important;
      }
    }

    input {
      @apply border-newBlue-400 truncate !important;
    }
  }
}

@screen md {
  .sub-title.ota-p16 {
    font-size: 1.8rem;
  }

  .ota-p14 {
    font-size: 1.4rem !important;
    line-height: 2.2rem !important;
  }
}
</style>
